/**
 * Sistema de Internacionalização (i18n) - Tipos
 * Suporta: pt-BR, en, ja
 */

export type Locale = "pt-BR" | "en" | "ja";

export type Currency = "BRL" | "USD" | "JPY";

export interface LocaleConfig {
  code: Locale;
  name: string;
  nativeName: string;
  flag: string;
  currency: Currency;
  currencySymbol: string;
  dateFormat: string;
}

export const LOCALES: Record<Locale, LocaleConfig> = {
  "pt-BR": {
    code: "pt-BR",
    name: "Portuguese (Brazil)",
    nativeName: "Português (Brasil)",
    flag: "🇧🇷",
    currency: "BRL",
    currencySymbol: "R$",
    dateFormat: "DD/MM/YYYY",
  },
  "en": {
    code: "en",
    name: "English",
    nativeName: "English",
    flag: "🇺🇸",
    currency: "USD",
    currencySymbol: "$",
    dateFormat: "MM/DD/YYYY",
  },
  "ja": {
    code: "ja",
    name: "Japanese",
    nativeName: "日本語",
    flag: "🇯🇵",
    currency: "JPY",
    currencySymbol: "¥",
    dateFormat: "YYYY/MM/DD",
  },
};

// Estrutura de traduções
export interface Translations {
  // Comum
  common: {
    continue: string;
    retry: string;
    ignore: string;
    loading: string;
    select: string;
    error: string;
    success: string;
    cancel: string;
    confirm: string;
    back: string;
    close: string;
    save: string;
    delete: string;
    edit: string;
    new: string;
    yes: string;
    no: string;
    or: string;
    and: string;
    show: string;
    hide: string;
    advanced: string;
    enabled: string;
    disabled: string;
  };
  
  // Menu principal
  menu: {
    profile: string;
    play: string;
    playAgain: string;
    shop: string;
    options: string;
    credits: string;
    referral: string;
    labs: string;
    dailyRewards: string;
    battlePass: string;
    creators: string;
    marketplace: string;
    social: string;
    clan: string;
    replays: string;
    // Play menu
    chooseMode: string;
    selectHowToPlay: string;
    playSolo: string;
    playSoloDesc: string;
    storyMode: string;
    storyModeDesc: string;
    multiplayer: string;
    multiplayerDesc: string;
    tournaments: string;
    tournamentsDesc: string;
    ranking: string;
    rankingDesc: string;
    experimental: string;
    experimentalDesc: string;
    competitive: string;
    competitiveDesc: string;
    // Seleção de modo solo
    infiniteBattle: string;
    infiniteBattleDesc: string;
    battleRoyaleDesc: string;
    genAIMode: string;
    genAIModeDesc: string;
    tagRoguelike: string;
    tagTerrains: string;
    tagElectricBarrier: string;
    tagUniqueItems: string;
    tagHot: string;
    tagNew: string;
    tagSkins: string;
  };

  // Perfil do jogador
  profile: {
    personalData: string;
    birthDate: string;
    showFullIdHint: string;
    copyId: string;
    copy: string;
    crossSaveTitle: string;
    crossSaveDesc: string;
    uniqueIdTitle: string;
    odysIdLabel: string;
    odysIdHint: string;
    session: string;
    switchAccount: string;
    switchAccountHint: string;
    avatarGallery: string;
    avatarGalleryDesc: string;
    avatarsUnlocked: string;
  };

  // Configuração de partida (solo / battle royale)
  matchSetup: {
    configureMatch: string;
    selectOpponents: string;
    tanksInArena: string;
    arenaSize: string;
    arenaSizeDesc: string;
    units: string;
    blocks: string;
    blocksDesc: string;
    blocksEstimate: string;
    perfLimited: string;
    densityNone: string;
    densityFew: string;
    densityMany: string;
    environment: string;
    environmentDesc: string;
    availableItems: string;
    powerUpsCount: string;
    itemsHint: string;
  };
  
  // Loja
  shop: {
    title: string;
    subtitle: string;
    // Tabs
    tabs: {
      skins: string;
      effects: string;
      crystals: string;
      mystery: string;
      tanks: string;
    };
    // Rarity filters
    rarity: {
      all: string;
      common: string;
      rare: string;
      epic: string;
      legendary: string;
    };
    // Mystery boxes
    mysteryBoxes: string;
    mysteryBoxesDesc: string;
    mysteryBox: {
      common: string;
      rare: string;
      epic: string;
      legendary: string;
    };
    mysteryBoxTip: string;
    // Crystals
    crystalPackages: string;
    crystalPremiumDesc: string;
    bonusAmount: string;
    redirectingPayment: string;
    securePaymentInfo: string;
    // Tanks
    tanks: string;
    tanksUnlocked: string;
    tanksFound: string;
    free: string;
    // Filters
    filters: string;
    filterRarity: string;
    filterSize: string;
    filterShots: string;
    sizeAll: string;
    sizeMini: string;
    sizeNormal: string;
    sizeLarge: string;
    sizeTitan: string;
    shotSingle: string;
    shotDouble: string;
    shotTriple: string;
    // Skins
    skins: string;
    skinsDesc: string;
    equip: string;
    equippedLabel: string;
    tankSkins: string;
    tankSkinsDesc: string;
    skinsCount: string;
    // Effects
    effects: string;
    effectsDesc: string;
    effectTypes: {
      trail: string;
      explosion: string;
      shot: string;
      aura: string;
      movement: string;
      special: string;
    };
    visualEffects: string;
    visualEffectsDesc: string;
    effectsCount: string;
    // Purchase modal
    tank: string;
    skin: string;
    effect: string;
    cancel: string;
    crystalDescription: string;
    popular: string;
    bonus: string;
    crystals: string;
    securePayment: string;
    paymentNotConfigured: string;
    // Compra
    confirmPurchase: string;
    price: string;
    yourBalance: string;
    insufficientBalance: string;
    buy: string;
    noBalance: string;
    use: string;
    active: string;
    equipped: string;
    removed: string;
    unlocked: string;
    chipsInsufficient: string;
    crystalsInsufficient: string;
    paymentCancelled: string;
    crystalsAdded: string;
    paymentUnavailable: string;
    openingCheckout: string;
    effectRemoved: string;
    generateNewCode: string;
    converted: string;
    // Footer
    chipsInfo: string;
  };
  
  // Labs
  ads: {
    watchForBonus: string;
  };
  labs: {
    hardcoreMode: string;
    title: string;
    subtitle: string;
    arMode: string;
    arModeDesc: string;
    vrMode: string;
    vrModeDesc: string;
    advancedAI: string;
    advancedAIDesc: string;
    openWorld: string;
    openWorldDesc: string;
    battleRoyale: string;
    battleRoyaleDesc: string;
    comingSoon: string;
    experimentalWarning: string;
    beta: string;
  };
  
  // AR Mode
  ar: {
    checking: string;
    unavailable: string;
    unavailableReason: string;
    tip: string;
    tipDevices: string[];
    startAR: string;
    startCamera: string;
    loading: string;
    exit: string;
    instructions: string;
    permission: string;
    permissionTitle: string;
    permissionDesc: string;
    cameraAccess: string;
    touchToMove: string;
    rotateCamera: string;
    title: string;
    notSupported: string;
    howItWorks: string;
    step1: string;
    step2: string;
    step3: string;
    placeBoard: string;
    placeInstructions: string;
    webARStep1: string;
    webARStep2: string;
    // Camera fallback mode
    cameraStep1: string;
    cameraStep2: string;
    modeWebXR: string;
    modeWebARSDK: string;
    modeCamera: string;
    tryCamera: string;
    openExternal: string;
    // Lista de dispositivos suportados
    deviceAndroid: string;
    deviceIOS: string;
    deviceQuest: string;
    // RocketAR Engine — Seleção de modo
    selectEngine: string;
    selectEngineDesc: string;
    recommendedBadge: string;
    unavailableBadge: string;
    selectMode: string;
    backToSelection: string;
    modeARCore: string;
    descWebXR: string;
    descARCore: string;
    descWebARSDK: string;
    descCamera: string;
    reasonNoNative: string;
    reasonNoWebXR: string;
    reasonNoSDK: string;
    reasonNoCamera: string;
  };
  
  // Gameplay
  game: {
    wave: string;
    lives: string;
    score: string;
    kills: string;
    pause: string;
    resume: string;
    quit: string;
    victory: string;
    defeat: string;
    nextWave: string;
    waveComplete: string;
    enemiesDefeated: string;
    bonus: string;
    total: string;
    gameOver: string;
    finalScore: string;
    play: string;
    playAgain: string;
    playSubtitle: string;
    // GameUI
    armor: string;
    targets: string;
    points: string;
    radar: string;
    item: string;
    charge: string;
    nominal: string;
    alert: string;
    critical: string;
    powerUp: string;
    cryo: string;
    shield: string;
    turbo: string;
    controls: string;
    controlsDesc: string;
    forward: string;
    reverse: string;
    left: string;
    right: string;
    clickToAim: string;
    speed: string;
    equipment: string;
    empty: string;
    heading: string;
    cameraToggle: string;
    // WaveComplete
    waveCompleteTitle: string;
    finalVictory: string;
    enemiesEliminated: string;
    pointsLabel: string;
    remainingLife: string;
    reward: string;
    nextWaveLabel: string;
    continueBtn: string;
    restartFromBeginning: string;
    hpRestored: string;
    backToMenu: string;
    // Dificuldade das ondas
    difficultyEasy: string;
    difficultyMedium: string;
    difficultyHard: string;
    difficultyVeryHard: string;
    difficultyExtreme: string;
    difficultyInsane: string;
    difficultyImpossible: string;
    // PauseOverlay
    gamePaused: string;
    storyPaused: string;
    youPaused: string;
    playerPaused: string;
    anotherPlayerPaused: string;
    tapToContinue: string;
    gamepadPauseHint: string;
    restart: string;
    restartCurrentRoom: string;
    pauseControls: string;
    confirmQuit: string;
    confirmRestart: string;
    retryRun: string;
    retryRunDesc: string;
    changeMode: string;
  };
  
  // Tank Selection
  tank: {
    equipped: string;
    chooseTank: string;
    roguelikeMode: string;
    speed: string;
    damage: string;
    health: string;
    unlocked: string;
    locked: string;
    startBattle: string;
    // Linhagens (famílias)
    family: string;
    familyPredator: string;
    familyColossus: string;
    familyVenom: string;
    familyStorm: string;
    familyPhantom: string;
    familyBulwark: string;
    familyCosmic: string;
    familyMythic: string;
    familyDescPredator: string;
    familyDescColossus: string;
    familyDescVenom: string;
    familyDescStorm: string;
    familyDescPhantom: string;
    familyDescBulwark: string;
    familyDescCosmic: string;
    familyDescMythic: string;
    // Poderes de linhagem (specials)
    special: string;
    specialElite: string;
    specialShield: string;
    specialStealth: string;
    specialRegen: string;
    specialExplosive: string;
    specialPiercing: string;
    specialHoming: string;
    specialRebirth: string;
    specialBurn: string;
    specialDescShield: string;
    specialDescStealth: string;
    specialDescRegen: string;
    specialDescExplosive: string;
    specialDescPiercing: string;
    specialDescHoming: string;
    specialDescRebirth: string;
    specialDescBurn: string;
    // Maestria
    mastery: string;
    masteryProgress: string;
    masteryMax: string;
    // Grade de tanques e preview
    speedShort: string;
    damageShort: string;
    healthShort: string;
    selected: string;
    select: string;
    tapToView: string;
    dragToRotate: string;
    availableToBuy: string;
    skinLabel: string;
    defaultSkin: string;
    activeSkinSolo: string;
    noSkin: string;
    unequipSkin: string;
    activeEffectSolo: string;
    noEffect: string;
    unequipEffect: string;
    skinIncompatible: string;
  };
  
  // Multiplayer
  multiplayer: {
    stepRoom: string;
    stepArena: string;
    stepWait: string;
    stepOf: string;
    createRoomDesc: string;
    joinRoomDesc: string;
    addBot: string;
    copyCode: string;
    createRoom: string;
    joinRoom: string;
    join: string;
    roomCode: string;
    enterCode: string;
    waiting: string;
    playersReady: string;
    startGame: string;
    leave: string;
    copied: string;
    shareCode: string;
    // Lobby
    title: string;
    subtitle: string;
    victoryTitle: string;
    defeatTitle: string;
    playAgain: string;
    winner: string;
    exit: string;
    shareWhatsApp: string;
    selectedArena: string;
    yourTank: string;
    yourColor: string;
    playersCount: string;
    you: string;
    host: string;
    gameMode: string;
    elimination: string;
    eliminationDesc: string;
    deathmatch: string;
    deathmatchDesc: string;
    timeLimit: string;
    timeLimitDesc: string;
    livesMode: string;
    livesModeDesc: string;
    battleRoyale: string;
    battleRoyaleDesc: string;
    battleRoyaleHint: string;
    brAliveCount: string;
    brZoneShrinking: string;
    brZoneNextShrink: string;
    killsToWin: string;
    timeLimitMinutes: string;
    livesPerPlayer: string;
    damage: string;
    speed: string;
    health: string;
    waitingPlayers: string;
    closeRoom: string;
    matchRules: string;
    waitingHost: string;
    leaveRoom: string;
    reconnecting: string;
    chooseArena: string;
    chooseArenaDesc: string;
    arenaLarge: string;
    arenaMedium: string;
    arenaStandard: string;
    random: string;
    randomOnCreate: string;
    randomArena: string;
    yourName: string;
    nameFromProfileHint: string;
    nextChooseArena: string;
    connectionLost: string;
    tryingToReconnect: string;
    tryingReconnect: string;
    disconnectedFromMatch: string;
    reconnect: string;
    ignore: string;
    waitingMorePlayers: string;
    loadingArena: string;
    matchStarting: string;
    countdownGo: string;
    creatingRoom: string;
    joining: string;
    connectingToServer: string;
    connectingServer: string;
    playersAndBots: string;
    maxPlayersLabel: string;
    botsFillSlots: string;
    none: string;
    botDifficulty: string;
    botEasy: string;
    botNormal: string;
    botHard: string;
    autoFillBots: string;
    removeBotsOnJoin: string;
    adaptiveBots: string;
    tankSpecials: string;
    tankSpecialsHint: string;
    itemsAndPowerups: string;
    enableAllItems: string;
    disableAllItems: string;
    noItemsEnabledWarning: string;
    botStatusSummary: string;
    botFineTuning: string;
    modeDeathmatchWithKills: string;
    modeTimedWithMinutes: string;
    modeLivesWithCount: string;
    spectatorModeTitle: string;
    spectatingPlayer: string;
    spectatorGenericPlayer: string;
    nextSpectator: string;
    exitRoom: string;
    alivePlayersTitle: string;
    errorOnlyHostRules: string;
    errorRulesWaitingOnly: string;
    errorNeedTwoPlayers: string;
    errorPlayersNotReady: string;
    errorOnlyHostResume: string;
    errorColorInUse: string;
    errorOnlyHostRestart: string;
    errorGameNotFinished: string;
    errorConnectFailed: string;
    errorCreateRoomFailed: string;
    errorRoomNotFound: string;
    myFriends: string;
    goToLobby: string;
    backToMenu: string;
  };

  // Battle Royale (telas de vitória/morte/espectador)
  br: {
    victory: string;
    champion: string;
    position: string;
    kills: string;
    players: string;
    score: string;
    reward: string;
    chips: string;
    claimReward: string;
    rewardClaimed: string;
    totalChips: string;
    backToMenu: string;
    itemsNote: string;
    eliminated: string;
    ofPlayers: string;
    spectate: string;
    menu: string;
    alive: string;
    zone: string;
    exit: string;
    winner: string;
    nextTarget: string;
    matchOver: string;
    noSurvivors: string;
    playAgain: string;
    itemShield: string;
    itemIntense: string;
    itemFreeze: string;
    itemHeal: string;
    itemMystery: string;
  };

  // Friends/Stats
  friends: {
    title: string;
    myFriends: string;
    matchesTogether: string;
    total: string;
    victories: string;
    defeats: string;
    winRate: string;
    yourKills: string;
    yourDeaths: string;
    kd: string;
    asAllies: string;
    matches: string;
    estimatedMovement: string;
    distanceInMatches: string;
    history: string;
    firstMatch: string;
    lastMatch: string;
    totalSessions: string;
    generalStats: string;
    list: string;
    yourJourney: string;
    knownPlayers: string;
    multiplayerMatches: string;
    performance: string;
    totalCombat: string;
    kills: string;
    deaths: string;
    totalDistance: string;
    distanceEstimate: string;
    recentMatches: string;
    noFriendsYet: string;
    playMultiplayer: string;
    recent: string;
    distance: string;
  };
  
  // Victory/MultiplayerVictory
  victory: {
    title: string;
    defeatTitle: string;
    yourStats: string;
    kills: string;
    deaths: string;
    finalScore: string;
    playAgain: string;
    goToLobby: string;
    backToMenu: string;
    won: string;
    winner: string;
  };

  // Opções
  options: {
    title: string;
    sound: string;
    music: string;
    sfx: string;
    vibration: string;
    language: string;
    graphics: string;
    low: string;
    medium: string;
    high: string;
    notifications: string;
    reset: string;
    resetConfirm: string;
    // Expanded options
    mute: string;
    active: string;
    generalVolume: string;
    effects: string;
    maxFps: string;
    camera: string;
    cameraTop: string;
    cameraAngled: string;
    cameraFirstPerson: string;
    aiDifficulty: string;
    disabled: string;
    haptics: string;
    notSupported: string;
    hapticIntensity: string;
    testVibration: string;
    // Voice Narration
    voiceNarration: string;
    voiceNarrationDesc: string;
    voiceVolume: string;
    beta: string;
    // Controls
    controls: string;
    controlsDesc: string;
    gamepadConnected: string;
    noGamepad: string;
    connectGamepadHint: string;
    mobileCompatibility: string;
    mobileAndroid: string;
    mobileIOS: string;
    mobileMapping: string;
    visualFeedback: string;
    leftStick: string;
    rightStick: string;
    buttonsPressed: string;
    noButtonPressed: string;
    controlMapping: string;
    movement: string;
    shoot: string;
    useItem: string;
    astra: string;
    astraHint: string;
    jump: string;
    dash: string;
    pause: string;
    touch: string;
    touchHint: string;
    gamepadSettings: string;
    deadzone: string;
    deadzoneHint: string;
    sensitivity: string;
    invertY: string;
    invertYHint: string;
    gamepadVibration: string;
    hideVirtualControls: string;
    unifiedControlsInfo: string;
    virtualJoystick: string;
  };
  
  // Dificuldade
  difficulty: {
    easy: string;
    easyDesc: string;
    normal: string;
    normalDesc: string;
    hard: string;
    hardDesc: string;
    extreme: string;
    extremeDesc: string;
  };
  
  // Gráficos
  graphics: {
    title: string;
    subtitle: string;
    quality: string;
    qualityLow: string;
    qualityMedium: string;
    qualityHigh: string;
    qualityDesc: string;
    explosions: string;
    advancedExplosions: string;
    advancedExplosionsDesc: string;
    explosionLights: string;
    shockwave: string;
    lighting: string;
    advancedLighting: string;
    softShadows: string;
    postProcessing: string;
    dynamicResolution: string;
    shadowQuality: string;
    effects: string;
    screenShake: string;
    screenShakeDesc: string;
    performance: string;
    dynamicOptimization: string;
    dynamicOptimizationDesc: string;
    renderBackend: string;
    webgpuUnavailable: string;
    webgpuActive: string;
    fpsTarget: string;
    resolution: string;
    fpsMonitor: string;
    fpsMonitorDesc: string;
    tensionMonitor: string;
    tensionMonitorDesc: string;
    bottomBar: string;
    bottomBarDesc: string;
    advancedSettings: string;
    advancedSettingsDesc: string;
    // Interface / movimento
    interfaceSection: string;
    quickOptions: string;
    autoDetectReset: string;
    recommended: string;
    motion: string;
    motionDesc: string;
    motionAuto: string;
    motionFull: string;
    motionReduced: string;
    activeEffects: string;
    autoReducedForFps: string;
    reducedMotionActive: string;
  };
  
  // Recompensas
  rewards: {
    daily: string;
    dailyDesc: string;
    claim: string;
    claimed: string;
    streak: string;
    day: string;
    comeBackTomorrow: string;
    // DailyRewards expanded
    week1: string;
    week2: string;
    week3: string;
    week4: string;
    megaFinal: string;
    enterDaily: string;
    record: string;
    currentDay: string;
    collect: string;
    nextSpecialReward: string;
    daysLeft: string;
    monthlyProgress: string;
    rewardCollected: string;
    comeBackTomorrowMore: string;
    // New labels
    title: string;
    sequence: string;
    xpBoost: string;
    chipsBoost: string;
    days: string;
    collectReward: string;
    daysInRow: string;
    thisMonth: string;
    pending: string;
  };
  
  // Battle Pass
  battlePass: {
    noActiveSubscription: string;
    title: string;
    subtitle: string;
    free: string;
    premium: string;
    level: string;
    xp: string;
    unlock: string;
    getPremium: string;
    // BattlePassView expanded
    mostPopular: string;
    choosePlan: string;
    allPlansInclude: string;
    cancelAnytime: string;
    collectReward: string;
    rewardCollected: string;
    passActive: string;
    timeRemaining: string;
    activatePremium: string;
    choosePlanDesc: string;
    missions: string;
    dailyMissions: string;
    dailyMissionsReset: string;
    weeklyMissions: string;
    weeklyMissionsReset: string;
    loadingMissions: string;
    // Subscription plans
    bronzePass: string;
    silverPass: string;
    goldPass: string;
    perMonth: string;
    crystalsPerDay: string;
    chipsPerDay: string;
    xpAndChipsBonus: string;
    noAds: string;
    extraDailyMission: string;
    exclusiveSkin: string;
    priorityQueue: string;
    subscribe: string;
    crystalsValue: string;
    collect: string;
    collected: string;
    season: string;
    ofLevel: string;
    backToMenu: string;
    rewards: string;
    // Subscription management
    manageSubscription: string;
    cancelSubscription: string;
    reactivateSubscription: string;
    subscriptionActive: string;
    subscriptionCanceled: string;
    expiresAt: string;
    managePayment: string;
    confirmCancel: string;
    reactivate: string;
    cancelsPeriodEnd: string;
    renewsAt: string;
    daysRemaining: string;
    crystalsReceived: string;
    subscriptionEnding: string;
    supportContact: string;
    active: string;
    canceling: string;
  };
  
  // Criadores
  creators: {
    title: string;
    enterCode: string;
    yourCode: string;
    totalReferrals: string;
    useCode: string;
    codeUsed: string;
    invalidCode: string;
    ownCode: string;
    share: string;
    copied: string;
    // CreatorCode expanded
    codeApplied: string;
    activeCode: string;
    enterCreatorCode: string;
    apply: string;
    becomeCreator: string;
    earnCommission: string;
    supportCreator: string;
    getDiscount: string;
    // CreatorDashboard
    creatorDashboard: string;
    yourCreatorCode: string;
    commission: string;
    discountForFans: string;
    creatorPoints: string;
    pendingValidation: string;
    pendingValidationDesc: string;
    verifiedCreator: string;
    verifiedCreatorDesc: string;
    registerAsCreator: string;
    overview: string;
    points: string;
    referrals: string;
    rewardsTab: string;
    // Dashboard Overview
    totalEarnings: string;
    totalUses: string;
    totalReferralsLabel: string;
    withdrawEarnings: string;
    minWithdrawal: string;
    topSupporters: string;
    tierBenefits: string;
    // Points Panel
    creatorPointsBalance: string;
    totalEarnedPoints: string;
    howToEarnPoints: string;
    convertPoints: string;
    convertPointsDesc: string;
    crystals: string;
    chips: string;
    convertPointsBtn: string;
    insufficientPoints: string;
    conversionHistory: string;
    // Referrals Panel
    activeReferrals: string;
    inviteFriends: string;
    inviteFriendsDesc: string;
    yourReferrals: string;
    yourReferralsDesc: string;
    noReferralsYet: string;
    shareCodeToStart: string;
    joinedOn: string;
    // Rewards Panel
    exclusiveCreatorItems: string;
    exclusiveCreatorItemsDesc: string;
    creatorSkin: string;
    creatorSkinDesc: string;
    creatorEffect: string;
    creatorEffectDesc: string;
    pixRewards: string;
    pixRewardsDesc: string;
    pixWithdraw: string;
    pixWithdrawDesc: string;
    pixMonthlyRaffle: string;
    pixMonthlyRaffleDesc: string;
    comingSoon: string;
    stayTuned: string;
    confirmRedemption: string;
    confirmRedemptionDesc: string;
    cancel: string;
    confirm: string;
    // Register Dialog
    registerAsCreatorTitle: string;
    registerAsCreatorDesc: string;
    validationNotice: string;
    followers: string;
    fillOneSocialMedia: string;
    registrationError: string;
    submitForReview: string;
  };
  
  // Referral
  referral: {
    title: string;
    yourCode: string;
    copied: string;
    copy: string;
    share: string;
    friendsUseCode: string;
    bothGet: string;
    friendCode: string;
    alreadyUsedCode: string;
    redeemAndEarn: string;
    enterCode: string;
    codePlaceholder: string;
    errorUsingCode: string;
    connectionError: string;
    shareMessage: string;
  };
  
  // Credits
  credits: {
    title: string;
    developer: string;
    mainTester: string;
    sponsors: string;
    sponsorsDesc: string;
    comingSoon: string;
    buyCrystals: string;
    allRightsReserved: string;
  };
  
  // Ranked/Leaderboard
  ranked: {
    profile: string;
    trophies: string;
    victories: string;
    defeats: string;
    winRate: string;
    highestTrophies: string;
    currentStreak: string;
    bestStreak: string;
    recentMatches: string;
    classification: string;
    global: string;
    myLeague: string;
    friends: string;
    yourPosition: string;
    noPlayersFound: string;
  };
  
  // Tournaments
  tournaments: {
    title: string;
    subtitle: string;
    selectToJoin: string;
    noTournamentAvailable: string;
    newTournamentSoon: string;
    players: string;
    open: string;
    almostFull: string;
    join: string;
    leaveTournament: string;
    entry: string;
    free: string;
    firstPlace: string;
    duration: string;
    minutes: string;
    tournaments: string;
    victories: string;
    top3: string;
    prizes: string;
    registering: string;
    inProgress: string;
    completed: string;
    waitingPlayers: string;
    round: string;
    final: string;
    participants: string;
    yourNextMatch: string;
    place: string;
    // Tier names
    bronze: string;
    silver: string;
    gold: string;
    diamond: string;
    // Errors
    unknownError: string;
    // Stats
    tournamentsPlayed: string;
    tournamentsWon: string;
    top3Rate: string;
    totalPrizeWon: string;
  };
  
  // Clan
  clan: {
    title: string;
    subtitle: string;
    noClan: string;
    noClanDesc: string;
    officialClans: string;
    officialClansFree: string;
    viewOfficialClans: string;
    searchClan: string;
    createClan: string;
    joinClan: string;
    leaveClan: string;
    // Roles
    leader: string;
    coLeader: string;
    elder: string;
    member: string;
    // Actions
    promote: string;
    demote: string;
    kick: string;
    // Time
    agoMinutes: string;
    agoHours: string;
    agoDays: string;
    perWeek: string;
    // Chat
    noMessages: string;
    clanMessage: string;
    // Stats
    trophies: string;
    donations: string;
    lastActive: string;
    members: string;
    clanLevel: string;
    clanWars: string;
    // Create
    clanName: string;
    clanTag: string;
    clanDescription: string;
    clanIcon: string;
    createClanBtn: string;
    // Search
    searchPlaceholder: string;
    noClansFound: string;
    availableClans: string;
    basedOnStoryMode: string;
    clanTypes: string;
    publicClans: string;
    publicClansDesc: string;
    privateClans: string;
    privateClansDesc: string;
    official: string;
    all: string;
    beginners: string;
    story: string;
    freeOfficialClansInfo: string;
    adminRevenueInfo: string;
    privateType: string;
    publicType: string;
    publicOfficialOnly: string;
    clanNamePlaceholder: string;
    clanTagPlaceholder: string;
    clanDescriptionPlaceholder: string;
    costSummary: string;
    createClanCost: string;
    memberFee: string;
    free: string;
    creating: string;
    info: string;
    wins: string;
    level: string;
    noDescription: string;
    warsComingSoon: string;
    inDevelopment: string;
  };
  
  // Replays
  replays: {
    title: string;
    subtitle: string;
    noReplays: string;
    noReplaysDesc: string;
    lastMatch: string;
    lastMatchDesc: string;
    importReplay: string;
    exportReplay: string;
    shareReplay: string;
    deleteReplay: string;
    play: string;
    pause: string;
    // Game modes
    survival: string;
    multiplayer: string;
    story: string;
    // Stats
    duration: string;
    players: string;
    wave: string;
    views: string;
    // Import/Export
    importTitle: string;
    importPlaceholder: string;
    import: string;
    codeCopied: string;
    // Viewer
    viewer: string;
    speed: string;
    stop: string;
    resume: string;
    replayEnded: string;
    dragTimeline: string;
    snapshot: string;
    events: string;
    // Tags
    tagMultiKill: string;
    tagClutch: string;
    tagPerfectWave: string;
    tagLongGame: string;
    tagHighScore: string;
    tagFlawless: string;
    // Toast
    shareCopied: string;
    exportCopied: string;
    shareError: string;
    exportError: string;
    deleteConfirm: string;
  };
  
  // Subscriptions
  subscription: {
    errorTitle: string;
    closeError: string;
    title: string;
    subtitle: string;
    free: string;
    freePlan: string;
    bronzePass: string;
    silverPass: string;
    goldPass: string;
    perMonth: string;
    // Benefits
    dailyCrystals: string;
    dailyChips: string;
    xpBonus: string;
    noAds: string;
    exclusiveSkin: string;
    priorityQueue: string;
    // Status
    dailyRewards: string;
    available: string;
    claimedToday: string;
    claimNow: string;
    claiming: string;
    claimed: string;
    // Management
    manageSubscription: string;
    upgrade: string;
    viewPlans: string;
    cancelSubscription: string;
    canceledAt: string;
    months: string;
    month: string;
    // Totals
    totalReceivedMonth: string;
    totalCrystals: string;
    totalChips: string;
    // Value
    crystalsValue: string;
    unlockDailyRewards: string;
    // SubscriptionCard dialog
    plansTitle: string;
    plansDescription: string;
    mostPopular: string;
    receiveDaily: string;
    currentPlan: string;
    processing: string;
    lowerPlan: string;
    subscribe: string;
    loginToSubscribe: string;
    confirmCancelMessage: string;
    reactivateSubscription: string;
    paymentSecure: string;
    cancelNoFee: string;
    rewardsAccumulate: string;
    instantUpgrade: string;
    claimedRewards: string;
    // Dynamic feature templates
    featureBattlePassPremium: string;
    featureCrystalsDaily: string;
    featureChipsDaily: string;
    featureXpChipsBonus: string;
    featureNoAds: string;
    featureExclusiveSkins: string;
    featurePriorityQueue: string;
    featureExtraMissions: string;
    featureFreeChipsDaily: string;
    featureBasicAccess: string;
    valueInCrystals: string;
  };
  
  // GenAI Victory Screen
  genAI: {
    // Personalities
    aggressive: string;
    defensive: string;
    strategic: string;
    player: string;
    // Victory messages
    battleComplete: string;
    youWon: string;
    youLost: string;
    aiVsAiMode: string;
    playerVsAiMode: string;
    model: string;
    winningModel: string;
    menu: string;
    playAgain: string;
  };
  
  // Ranked Profile
  rankedProfile: {
    title: string;
    trophies: string;
    trophiesToNextTier: string;
    victories: string;
    defeats: string;
    winRate: string;
    highestTrophies: string;
    currentStreak: string;
    bestStreak: string;
    recentMatches: string;
    winsLabel: string;
  };
  
  // Marketplace
  marketplace: {
    explore: string;
    create: string;
    myItems: string;
    history: string;
    createBuySell: string;
    sale: string;
    auction: string;
    trade: string;
    free: string;
    // New labels
    title: string;
    subtitle: string;
    searchItems: string;
    noItemsFound: string;
    adjustFilters: string;
    all: string;
    skins: string;
    effects: string;
    tanks: string;
    badges: string;
    backToMenu: string;
    feeInfo: string;
    // Tabs
    tabs: {
      browse: string;
      flash: string;
      bundles: string;
      create: string;
      myItems: string;
      profile: string;
      history: string;
    };
    // Fees
    fees: {
      sale: string;
      auction: string;
      trade: string;
      royalties: string;
      level: string;
      discount: string;
    };
    // Content creation
    createContent: string;
    createContentDesc: string;
    contentType: string;
    itemName: string;
    itemDescription: string;
    skinColor: string;
    visualEffect: string;
    sticker: string;
    skinColorDesc: string;
    visualEffectDesc: string;
    stickerDesc: string;
    colors: string;
    gradient: string;
    primaryColor: string;
    secondaryColor: string;
    preview: string;
    uploadImage: string;
    clickToUpload: string;
    clickToChange: string;
    imageRules: string;
    moderationWarning: string;
    submitForApproval: string;
    submitting: string;
    submitted: string;
    itemSentModeration: string;
    moderationNotify: string;
    // Content rules
    contentRules: string;
    originalOnly: string;
    noOffensive: string;
    noLogos: string;
    moderationTime: string;
    // My items tabs
    selling: string;
    sold: string;
    pending: string;
    // Listing status
    active: string;
    expired: string;
    soldStatus: string;
    // Transaction history
    totalEarned: string;
    totalSpent: string;
    noTransactions: string;
    soldTo: string;
    boughtFrom: string;
    // Listing details
    price: string;
    currentBid: string;
    yourBid: string;
    minBid: string;
    buyNow: string;
    placeBid: string;
    processing: string;
    insufficientCrystals: string;
    purchasesUnavailable: string;
    cancel: string;
    // Flash sales
    noFlashSales: string;
    flashSalesDesc: string;
    endsIn: string;
    remaining: string;
    soldOut: string;
    buy: string;
    unavailable: string;
    // Featured
    official: string;
    officialDesc: string;
    viewItems: string;
    createYourSkins: string;
    createYourSkinsDesc: string;
    // Validation errors
    nameMinChars: string;
    nameBannedWord: string;
    invalidColor: string;
    imageRequired: string;
    fileTooLarge: string;
    invalidImage: string;
    imageProcessError: string;
    // Offer
    offer: string;
    // Type badges
    saleType: string;
    auctionType: string;
    tradeType: string;
    itemSentForModeration: string;
    willBeNotified: string;
    rule1: string;
    rule2: string;
    rule3: string;
    rule4: string;
    noItemsForSale: string;
    noItemsSold: string;
    noItemsPending: string;
    purchasedFrom: string;
    purchasesDisabledWarning: string;
    purchasesUnavailableShort: string;
    purchaseFailed: string;
    bundlesAndCollections: string;
    bundlesDesc: string;
    createBundle: string;
    noBundles: string;
    noBundlesDesc: string;
    starterPack: string;
    seasonal: string;
    themed: string;
    exclusive: string;
    buyBundle: string;
    verificationSent: string;
    verificationFailed: string;
    save: string;
    noDescription: string;
    edit: string;
    followers: string;
    sales: string;
    sellerLevel: string;
    progressToLevel: string;
    xpToNextLevel: string;
    maxLevelReached: string;
    feeDiscount: string;
    activeListings: string;
    auctions: string;
    bundles: string;
    creatorStats: string;
    contentsCreated: string;
    downloads: string;
    likes: string;
    royaltiesEarned: string;
    requestVerification: string;
    verificationBenefits: string;
    creator: string;
    creatorReq: string;
    artist: string;
    artistReq: string;
    streamer: string;
    streamerReq: string;
  };
  
  // Shop Effects
  shopEffects: {
    trail: string;
    explosion: string;
    shot: string;
    aura: string;
    movement: string;
    special: string;
  };
  
  // General UI
  ui: {
    dailyRewards: string;
    changeTank: string;
    summary: string;
    battles: string;
    wins: string;
    kills: string;
    winRate: string;
    touchToStart: string;
    starting: string;
    mobileControls: string;
    desktopControls: string;
    shopAndRewards: string;
    shop: string;
    rewards: string;
    creators: string;
    refer: string;
    // Update notification
    newVersionAvailable: string;
    updateReady: string;
    updating: string;
    updateNow: string;
    later: string;
    // Error boundary
    somethingWentWrong: string;
    unknownError: string;
    reloadPage: string;
    technicalDetails: string;
    screenLoadError: string;
    backToMenu: string;
    // Dock
    chips: string;
    crystals: string;
    missions: string;
    notifications: string;
    viewList: string;
    viewCards: string;
    // Chapters
    comingSoon: string;
    replay: string;
    play: string;
    // Conexão
    offline: string;
  };
  
  // Story/Dialogue
  story: {
    // Fim de partida no modo história
    missionFailed: string;
    missionFailedDesc: string;
    tryAgain: string;
    backToChapters: string;
    exitStory: string;
    tapToContinue: string;
    chapter: string;
    mode: string;
    // Story Mode page
    title: string;
    subtitle: string;
    backToMenu: string;
    prologueLabel: string;
    prologueTitle: string;
    prologueDesc: string;
    // Chapters
    chapters: {
      awakening: string;
      awakeningDesc: string;
      firstConflict: string;
      firstConflictDesc: string;
      rescue: string;
      rescueDesc: string;
      iceStorm: string;
      iceStormDesc: string;
      darkForest: string;
      darkForestDesc: string;
      finalVolcano: string;
      finalVolcanoDesc: string;
      shadowsOfPast: string;
      shadowsOfPastDesc: string;
      iceAndSilence: string;
      iceAndSilenceDesc: string;
      theTraitor: string;
      theTraitorDesc: string;
      prime: string;
      primeDesc: string;
    };
  };
  
  // Saudações
  greetings: {
    morning: string;
    afternoon: string;
    evening: string;
    lateNight: string;
    restMessage1: string;
    restMessage2: string;
    restMessage3: string;
    // Mensagens de progresso
    firstBattle: string;
    startingWell: string;
    keepGoing: string;
    firstWin: string;
    moreWins: string;
    gettingBetter: string;
    skilled: string;
    veteran: string;
    legend: string;
    master: string;
    unstoppable: string;
    warMachine: string;
    richInChips: string;
  };
  
  // Erros
  errors: {
    connectionFailed: string;
    serverError: string;
    timeout: string;
    invalidInput: string;
    permissionDenied: string;
    notFound: string;
    cameraError: string;
    cameraInUse: string;
    cameraNotFound: string;
    httpsRequired: string;
  };
  
  // Autenticação / Login
  auth: {
    tapToPlay: string;
    welcome: string;
    chooseLoginMethod: string;
    loginWithGoogle: string;
    loginWithDiscord: string;
    loginWithEmail: string;
    comingSoon: string;
    unavailable: string;
    emailCodeSent: string;
    emailCodePrompt: string;
    sendCode: string;
    sending: string;
    verifying: string;
    confirmAndLogin: string;
    useAnotherEmail: string;
    useAnotherAccount: string;
    completeProfile: string;
    profileInfoNeeded: string;
    email: string;
    fullName: string;
    fullNamePrivate: string;
    publicAlias: string;
    aliasChars: string;
    birthDate: string;
    yourName: string;
    yourAlias: string;
    yourEmail: string;
    saving: string;
    saveAndPlay: string;
    welcomeBack: string;
    playNow: string;
    starting: string;
    termsAgree: string;
    termsOfUse: string;
    privacyPolicy: string;
    aliasInUse: string;
    aliasInvalid: string;
    // Bio / Profile
    bio: string;
    bioPlaceholder: string;
    clickToAddBio: string;
    change: string;
    // Validation errors
    invalidEmail: string;
    fullNameMinLength: string;
    aliasMinLength: string;
    birthDateRequired: string;
    profileSaveFailed: string;
    connectionError: string;
  };
  
  // Admin Panel
  admin: {
    title: string;
    loginError: string;
    playerNotFound: string;
    announcementPlaceholder: string;
    search: string;
    searchPlayer: string;
    wins: string;
    systemSettings: string;
    refreshData: string;
    settingLocked: string;
    dailyRewards: string;
    successRate: string;
    playerUUID: string;
    minAge: string;
    maxAge: string;
    resetMemoriesConfirm: string;
    // GenAI section
    genaiLearning: string;
    totalMemories: string;
    totalMatches: string;
    uniquePlayers: string;
    topPlayers: string;
    recentMatches: string;
    resetPlayerMemory: string;
    resetAllMemories: string;
    noMemoriesYet: string;
    // Other sections
    announcements: string;
    players: string;
    dashboard: string;
    config: string;
  };
  
  // Legal pages (Terms of Use, Privacy Policy)
  legal: {
    // Common
    back: string;
    lastUpdated: string;
    contactUs: string;
    generalSupport: string;
    privacyEmail: string;
    companyInfo: string;
    legalQuestions: string;
    introduction: string;
    
    // Terms of Use page
    termsTitle: string;
    termsSubtitle: string;
    termsHeroTitle: string;
    termsHeroDesc: string;
    termsSummary: string;
    termsSummaryDesc: string;
    
    // Terms intro
    termsWelcome: string;
    termsWelcomeDesc: string;
    termsAgeTitle: string;
    termsAgeDesc: string;
    
    // Terms sections titles
    termsLicense: string;
    termsAccount: string;
    termsConduct: string;
    termsPurchases: string;
    termsIntellectual: string;
    termsAvailability: string;
    termsLiability: string;
    termsPenalties: string;
    termsChanges: string;
    termsLaw: string;
    
    // Terms License content
    termsLicenseDesc: string;
    termsYouCan: string;
    termsYouCanPlay: string;
    termsYouCanPurchase: string;
    termsYouCanCreate: string;
    termsYouCanParticipate: string;
    termsYouCannot: string;
    termsYouCannotModify: string;
    termsYouCannotSell: string;
    termsYouCannotMultiple: string;
    termsYouCannotBots: string;
    
    // Terms Account content
    termsAccountDesc: string;
    termsAccountSecurity: string;
    termsAccountSecurityShare: string;
    termsAccountSecurityAuth: string;
    termsAccountSecurityReport: string;
    termsAccountName: string;
    termsAccountNameRespect: string;
    termsAccountNameOffensive: string;
    termsAccountNameChange: string;
    termsAccountWarning: string;
    
    // Terms Conduct content
    termsCondictDesc: string;
    termsRespectPlayers: string;
    termsRespectPlayersDesc: string;
    termsPlayFair: string;
    termsPlayFairDesc: string;
    termsReportViolations: string;
    termsReportViolationsDesc: string;
    termsNoToxicity: string;
    termsNoToxicityDesc: string;
    termsNoSpam: string;
    termsNoSpamDesc: string;
    
    // Terms Purchases content
    termsCurrencies: string;
    termsCurrenciesDesc: string;
    termsPurchasesFinal: string;
    termsPricesChange: string;
    termsVirtualItems: string;
    termsNoRefundShutdown: string;
    termsConsumerProtection: string;
    termsConsumerProtectionDesc: string;
    
    // Terms Intellectual content
    termsIntellectualDesc: string;
    termsContentCreation: string;
    termsContentCreationDesc: string;
    termsContentNotOfficial: string;
    termsContentNotMonetize: string;
    termsContentCredit: string;
    
    // Terms Availability content
    termsAvailabilityDesc: string;
    termsMaintenanceNotice: string;
    termsServiceModify: string;
    termsProgressLoss: string;
    
    // Terms Liability content
    termsLiabilityDesc: string;
    termsNoBugGuarantee: string;
    termsNoIndirectDamage: string;
    termsMaxLiability: string;
    termsLiabilityImportant: string;
    termsLiabilityImportantDesc: string;
    
    // Terms Penalties content
    termsPenaltiesDesc: string;
    termsWarning: string;
    termsWarningDesc: string;
    termsSuspension: string;
    termsSuspensionDesc: string;
    termsBan: string;
    termsBanDesc: string;
    termsPenaltiesSeverity: string;
    
    // Terms Changes content
    termsChangesDesc: string;
    termsChangesNotify: string;
    termsChangesDate: string;
    termsChangesAccept: string;
    
    // Terms Law content
    termsLawDesc: string;
    termsLawCourt: string;
    termsFriendlyResolution: string;
    termsFriendlyResolutionDesc: string;
    
    // Terms Summary content
    termsSummaryPlay: string;
    termsSummaryBeNice: string;
    termsSummaryPlayFair: string;
    termsSummaryFinal: string;
    termsSummaryContact: string;
    
    // Privacy Policy page
    privacyTitle: string;
    privacySubtitle: string;
    privacyHeroTitle: string;
    privacyHeroDesc: string;
    
    // Privacy intro
    privacyIntro: string;
    privacyIntroDesc: string;
    
    // Privacy sections titles
    privacyCollection: string;
    privacyUsage: string;
    privacySecurity: string;
    privacyRights: string;
    privacyCookies: string;
    privacyMinors: string;
    privacyChanges: string;
    
    // Privacy Collection content
    privacyAccountData: string;
    privacyAccountEmail: string;
    privacyAccountUsername: string;
    privacyAccountBirth: string;
    privacyAccountAvatar: string;
    privacyGameData: string;
    privacyGameProgress: string;
    privacyGameStats: string;
    privacyGameSettings: string;
    privacyGamePurchases: string;
    privacyTechData: string;
    privacyTechDevice: string;
    privacyTechIP: string;
    privacyTechLogs: string;
    
    // Privacy Usage content
    privacyUsageImprove: string;
    privacyUsageSave: string;
    privacyUsagePayments: string;
    privacyUsageNotify: string;
    privacyUsageFraud: string;
    privacyUsageMatchmaking: string;
    privacyNeverSell: string;
    privacyNeverShare: string;
    
    // Privacy Security content
    privacySecurityDesc: string;
    privacySecurityTLS: string;
    privacySecurityTLSDesc: string;
    privacySecurityDB: string;
    privacySecurityDBDesc: string;
    privacySecurityTokens: string;
    privacySecurityTokensDesc: string;
    privacySecurityMonitor: string;
    privacySecurityMonitorDesc: string;
    
    // LGPD Rights (Brazil)
    lgpdRights: string;
    lgpdAccess: string;
    lgpdAccessDesc: string;
    lgpdCorrection: string;
    lgpdCorrectionDesc: string;
    lgpdDeletion: string;
    lgpdDeletionDesc: string;
    lgpdPortability: string;
    lgpdPortabilityDesc: string;
    lgpdRevocation: string;
    lgpdRevocationDesc: string;
    lgpdExercise: string;
    
    // Privacy Cookies content
    privacyCookiesDesc: string;
    privacyCookiesLogin: string;
    privacyCookiesSettings: string;
    privacyCookiesCache: string;
    privacyCookiesNoTracking: string;
    
    // Privacy Minors content
    privacyMinorsAge: string;
    privacyMinorsImportant: string;
    privacyMinorsConsent: string;
    privacyMinorsNoCollect: string;
    privacyMinorsPurchases: string;
    
    // Privacy Changes content
    privacyChangesDesc: string;
    privacyChangesNotify: string;
    privacyChangesDate: string;
    privacyChangesEffective: string;
  };

  // Batalha Infinita
  //
  // Cartas, contratos e arquétipos usam Record em vez de campo por campo: são
  // dezenas de entradas e crescem a cada carta nova. Declarar cada id no tipo
  // obrigaria a editar a interface e os três idiomas a cada ajuste de baralho,
  // sem ganhar nada — as chaves já são validadas pelos testes do baralho.
  infinite: {
    bestWave: string;
    newRecord: string;
    noRecordYet: string;
    medals: string;
    nextMedal: string;
    statusBurning: string;
    statusSoaked: string;
    statusMuddy: string;
    statusHint: string;
    contract: string;
    contractComplete: string;
    contractFailed: string;
    contractReward: string;
    chooseUpgrade: string;
    chooseUpgradeHint: string;
    reroll: string;
    rerollsLeft: string;
    skipDraft: string;
    yourBuild: string;
    waveLabel: string;
    bossWaveIn: string;
    bossWaveNow: string;
    eliteWave: string;
    hazardWarning: string;
    tapToSkip: string;
    runSummary: string;
    contractsCleared: string;
    cards: Record<string, { title: string; description: string }>;
    contracts: Record<string, { title: string; description: string }>;
    enemyKinds: Record<string, string>;
    archetypes: Record<string, string>;
    worldModifiers: Record<string, { title: string; description: string }>;
    hazards: Record<string, string>;
  };
  // Modo Sofá (2 jogadores, tela dividida)
  sofa: {
    menuTitle: string;
    menuDesc: string;
    title: string;
    subtitle: string;
    player: string;
    keyboardJoin: string;
    joined: string;
    leaveHint: string;
    modeCoop: string;
    modeCoopDesc: string;
    modeVersus: string;
    modeVersusDesc: string;
    arena: string;
    back: string;
    tvTitle: string;
    tvStep1: string;
    tvStep2: string;
    tvStep3: string;
    noGamepadApi: string;
    keyboardWasd: string;
    keyboardArrows: string;
    wave: string;
    firstTo: string;
    getReady: string;
    go: string;
    waveCleared: string;
    nextWaveIn: string;
    respawnIn: string;
    paused: string;
    quit: string;
    pauseHint: string;
    disconnected: string;
    reconnectHint: string;
    victory: string;
    reachedWave: string;
    rematch: string;
    overHint: string;
    lives: string;
    modeRivals: string;
    modeRivalsDesc: string;
    difficulty: string;
    diffEasy: string;
    diffNormal: string;
    diffHard: string;
    killsLabel: string;
    noSpecial: string;
    score: string;
    waitingRescue: string;
    rescuing: string;
    annKilled: string;
    annDown: string;
    annEliminated: string;
    annRescued: string;
    annBoss: string;
    annBossDown: string;
    annPickup: string;
    hintPad: string;
    hintWasd: string;
    hintArrows: string;
    accuracy: string;
    damage: string;
    reward: string;
    newRecord: string;
    draw: string;
    stepJoin: string;
    stepTanks: string;
    stepSetup: string;
    stepOf: string;
    joinTitle: string;
    joinText: string;
    continue: string;
    continueHint: string;
    confirmTank: string;
    tanksTitle: string;
    tanksText: string;
    tanksPadHint: string;
    tanksKeysHint: string;
    killsShort: string;
    setupTitle: string;
    setupText: string;
    modeLabel: string;
    startMatch: string;
    setupHint: string;
    leave: string;
    prevPower: string;
    nextPower: string;
    pairTitle: string;
    pairWhereIos: string;
    pairWhereAndroid: string;
    pairWhereDesktop: string;
    pairBrandOther: string;
    pairXbox: string;
    pairPlaystation: string;
    pairNintendo: string;
    pairOther: string;
    pairThen: string;
    pairNote: string;
    diagTitle: string;
    diagHint: string;
    diagApiOk: string;
    diagNoPads: string;
    diagStandard: string;
    diagNonStandard: string;
    diagRumble: string;
    diagButtons: string;
    diagShowFps: string;
    cpuName: string;
    map: string;
    slotHuman: string;
    slotOff: string;
    slotTakeHint: string;
    slotClickHint: string;
    keyboardPlay: string;
    keyboardPlayHint: string;
    slotAdd: string;
    playersLabel: string;
    cpuLevel: string;
    needHuman: string;
    summaryPlayers: string;
    summaryCpu: string;
    playersPadHint: string;
    playersKeysHint: string;
    shuffle: string;
    readyToFight: string;
    readyToFightHint: string;
  };
}

// Tipo para chaves de tradução aninhadas
export type TranslationKey = 
  | `common.${keyof Translations["common"]}`
  | `menu.${keyof Translations["menu"]}`
  | `shop.${keyof Translations["shop"]}`
  | `labs.${keyof Translations["labs"]}`
  | `ar.${keyof Translations["ar"]}`
  | `game.${keyof Translations["game"]}`
  | `tank.${keyof Translations["tank"]}`
  | `multiplayer.${keyof Translations["multiplayer"]}`
  | `friends.${keyof Translations["friends"]}`
  | `victory.${keyof Translations["victory"]}`
  | `options.${keyof Translations["options"]}`
  | `rewards.${keyof Translations["rewards"]}`
  | `battlePass.${keyof Translations["battlePass"]}`
  | `creators.${keyof Translations["creators"]}`
  | `referral.${keyof Translations["referral"]}`
  | `credits.${keyof Translations["credits"]}`
  | `ranked.${keyof Translations["ranked"]}`
  | `tournaments.${keyof Translations["tournaments"]}`
  | `clan.${keyof Translations["clan"]}`
  | `replays.${keyof Translations["replays"]}`
  | `subscription.${keyof Translations["subscription"]}`
  | `marketplace.${keyof Translations["marketplace"]}`
  | `shopEffects.${keyof Translations["shopEffects"]}`
  | `ui.${keyof Translations["ui"]}`
  | `story.${keyof Translations["story"]}`
  | `greetings.${keyof Translations["greetings"]}`
  | `errors.${keyof Translations["errors"]}`
  | `auth.${keyof Translations["auth"]}`
  | `admin.${keyof Translations["admin"]}`
  | `legal.${keyof Translations["legal"]}`
  | `infinite.${keyof Translations["infinite"]}`
  | `sofa.${keyof Translations["sofa"]}`;
